import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faSave } from '@fortawesome/pro-duotone-svg-icons';
import { toast } from 'react-toastify';
import moment from 'moment';


// Components
import { Spinner } from '../../../../common/components';
import { showPopup } from '../../../../utils/popup';

// Actions
import { getFilePatterns } from '../../../../actions/rts';
import { createPermission, updatePermission } from '../../../../actions/permissions';


function Button({
  label = '',
  id = '',
  extraClasses = '',
  buttonWidth = '',
  value = '',
  state = false,
  disabled = false,
}) {
  const dispatch = useDispatch();
  const { states = [] } = useSelector(store => store.permissions.carrier.newEntry);

  const toggle = value ? 'btn-success' : 'btn-light';

  function handleToggle() {
    dispatch({
      type: 'EDIT_NEW_PERMISSION',
      level: 'carrier',
      id,
      value: !value,
    });
  }

  function handleStateToggle() {
    let statesCopy = [ ...states ];
    if (value) statesCopy = statesCopy.filter(s => s !== id);
    else statesCopy.push(id);
    dispatch({
      type: 'EDIT_NEW_PERMISSION',
      level: 'carrier',
      id: 'states',
      value: statesCopy,
    });
  }

  return (
    <button
      type="button"
      className={`btn ${toggle} ${extraClasses}`}
      id={id}
      style={{ borderColor: 'darkgray', width: buttonWidth }}
      onClick={() => (state ? handleStateToggle() : handleToggle())}
      value={value}
      disabled={disabled}
    >
      {label}
    </button>
  );
}

export default function AddEditCarrierPermissions({ user = {} }) {
  const dispatch = useDispatch();
  const {
    username,
    first_name: props_first_name,
    last_name: props_last_name,
    middle_name: props_middle_name,
    npn: props_npn,
  } = user;
  const { rts: { patterns } } = useSelector(store => store);
  const {
    carrierPermissionCarriers,
  } = useSelector(store => store.theme);
  const {
    editing,
    saving,
    erroring,
    newEntry,
  } = useSelector(store => store.permissions.carrier);
  const {
    plan_year,
    carrier_id,
    npn,
    file_family_id,
    carrier_agent_id,
    product_ma,
    product_mapd,
    product_pdp,
    product_medigap,
    states = [],
    id: identity = '',
    first_name,
    middle_name,
    last_name,
    encrypted_fein,
    active_date,
    active,
    agent_type,
  } = newEntry;
  const [ filteredPatterns, setFilteredPatterns ] = useState([]);

  const emptyFields = !plan_year || !carrier_id || !npn || !carrier_agent_id || !active || !active_date || !file_family_id;
  const allStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA',
    'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY',
    'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX',
    'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  ];
  const currentYear = Number(moment().format('YYYY'));
  const planYears = [currentYear + 1, currentYear, currentYear - 1];

  useEffect(() => {
    getFilePatterns()
      .then((response) => dispatch({ type: 'SET_RTS_PATTERNS', patterns: response }));
    if (!npn) {
      dispatch({
        type: 'EDIT_NEW_PERMISSION',
        level: 'carrier',
        value: props_npn,
        id: 'npn',
      });
    }
  }, []);


  useEffect(() => {
    // eslint-disable-next-line eqeqeq
    setFilteredPatterns(patterns.filter(pattern => pattern.carrierID == carrier_id));
  }, [carrier_id]);

  function handleSaveClick() {
    dispatch({ type: 'CARRIER_PERMISSIONS_SAVING' });
    if (editing) {
      const filteredData = {
        npn,
        plan_year,
        carrier_id,
        states,
        product_ma,
        product_mapd,
        product_pdp,
        product_medigap,
        carrier_agent_id,
        file_family_id,
        first_name,
        middle_name,
        last_name,
        encrypted_fein,
        active_date,
        active,
        agent_type,
      };
      if (!filteredData.file_family_id) filteredData.file_family_id = undefined;
      updatePermission(identity, 'carrier', filteredData)
        .then(() => {
          showPopup('Changes to the carrier permission are saved!', 'Note: The RTS uploads supersede manual entry.');
          dispatch({ type: 'SET_CARRIER_PERMISSIONS_PAGE', page: 'carrierTable' });
          dispatch({ type: 'CARRIER_PERMISSIONS_DONE_SAVING' });
        }).catch((err) => {
          // need to add custom error handling
          if (err.response && err.response.status === 409) {
            toast.error('We are unable to update the email address as this email address is already in use.');
          } else {
            toast.error('Error updating Agent');
          }
          dispatch({ type: 'CARRIER_PERMISSIONS_DONE_SAVING' });
        });
    } else {
      const saveData = { ...newEntry };
      saveData.first_name = props_first_name;
      saveData.middle_name = props_middle_name;
      saveData.last_name = props_last_name;
      saveData.agent_type = '';
      if (!saveData.file_family_id) saveData.file_family_id = undefined;
      createPermission(username, 'carrier', saveData)
        .then(() => {
          showPopup('Changes to the carrier permission are saved!', 'Note: The RTS uploads supersede manual entry.');
          dispatch({ type: 'SET_CARRIER_PERMISSIONS_PAGE', page: 'carrierTable' });
          dispatch({ type: 'CARRIER_PERMISSIONS_DONE_SAVING' });
        }).catch((err) => {
          // need to add custom error handling
          if (err.response && err.response.status === 409) {
            toast.error('We are unable to update the email address as this email address is already in use.');
          } else {
            toast.error('Error updating Agent');
          }
          dispatch({ type: 'CARRIER_PERMISSIONS_DONE_SAVING' });
        });
    }
  }

  function handleChange(event) {
    const { id, value } = event.target;
    dispatch({
      type: 'EDIT_NEW_PERMISSION',
      level: 'carrier',
      id,
      value,
    });
    if (id === 'carrier_id') {
      dispatch({
        type: 'EDIT_NEW_PERMISSION',
        level: 'carrier',
        id: 'file_family_id',
        value: '',
      });
    }
  }

  function handleAllProducts(e) {
    const products = ['product_ma', 'product_mapd', 'product_pdp'/* , 'product_medigap' */]; // note (Jason): medigap is currently disabled
    if (e.target.id === 'product_select_all') {
      products.forEach((p) => {
        dispatch({
          type: 'EDIT_NEW_PERMISSION',
          level: 'carrier',
          id: p,
          value: true,
        });
      });
    } else {
      products.forEach((p) => {
        dispatch({
          type: 'EDIT_NEW_PERMISSION',
          level: 'carrier',
          id: p,
          value: false,
        });
      });
    }
  }

  function handleAllStates(e) {
    if (e.target.id === 'states_select_all') {
      dispatch({
        type: 'EDIT_NEW_PERMISSION',
        level: 'carrier',
        id: 'states',
        value: allStates,
      });
    } else {
      dispatch({
        type: 'EDIT_NEW_PERMISSION',
        level: 'carrier',
        id: 'states',
        value: [],
      });
    }
  }

  return (
    <>
      <div className="d-flex justify-content-between mb-3">
        <div>
          <h3>{editing ? 'Edit Carrier Permission' : 'Create New Carrier Permission'}</h3>
        </div>
        <div className="align-self-end fade-in">
          <button
            type="button"
            className="btn btn-danger me-3"
            onClick={() => dispatch({ type: 'SET_CARRIER_PERMISSIONS_PAGE', page: 'carrierTable' })}
          >
            <FontAwesomeIcon icon={faTimes} size="lg" className="me-2" />
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={() => handleSaveClick()}
            disabled={saving || erroring || emptyFields}
          >
            <FontAwesomeIcon icon={faSave} size="lg" className="me-2" />
            Save Changes
            {saving && (
              <Spinner size="sm" className="ms-2" />
            )}
          </button>
        </div>
      </div>

      <div className="border border-light">
        <div className="fs-4 pt-3 ps-2 row">
          <div className="col-6">
            <div className="d-flex row align-items-center my-2">
              <div className="col-1" style={{ minWidth: '10.1rem' }}>
                <label htmlFor="plan_year">
                  Plan Year<span className="text-danger">*</span>
                </label>
              </div>
              <div className="col-auto">
                <select
                  id="plan_year"
                  className="form-select"
                  value={plan_year || ''}
                  onChange={(e) => handleChange(e)}
                >
                  <option value="" />
                  {planYears.map((p) => (
                    <option value={p} key={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="d-flex row align-items-center my-2">
              <div className="col-1" style={{ minWidth: '10.1rem' }}>
                <label htmlFor="carrier_id">
                  Carrier<span className="text-danger">*</span>
                </label>
              </div>
              <div className="col-auto">
                <select
                  id="carrier_id"
                  className="form-select"
                  value={carrier_id || ''}
                  onChange={(e) => handleChange(e)}
                  disabled={editing}
                >
                  <option value="" />
                  {carrierPermissionCarriers.map(c => (
                    <option value={c.id} key={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="d-flex row align-items-center my-2">
              <div className="col-auto" style={{ minWidth: '10.1rem' }}>
                <label htmlFor="file_family_id">
                  File Family ID<span className="text-danger">*</span>
                </label>
              </div>
              <div className="col-auto">
                <select
                  id="file_family_id"
                  className="form-select"
                  value={file_family_id || ''}
                  onChange={(e) => handleChange(e)}
                  disabled={!carrier_id}
                >
                  <option value={undefined} />
                  {filteredPatterns.map((f) => (
                    <option value={f.familyID} key={f.familyID}>{f.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="d-flex row align-items-center my-2">
              <div className="col-auto" style={{ minWidth: '10.1rem' }}>
                <label htmlFor="npn">
                  Agent NPN<span className="text-danger">*</span>
                </label>
              </div>
              <div className="col-auto">
                <input
                  type="text"
                  id="npn"
                  className="form-control"
                  value={npn || props_npn}
                  onChange={(e) => handleChange(e)}
                />
              </div>
            </div>
            <div className="d-flex row align-items-center my-2">
              <div className="col-auto" style={{ minWidth: '10.1rem' }}>
                <label htmlFor="carrier_agent_id">
                  Carrier Agent ID<span className="text-danger">*</span>
                </label>
              </div>
              <div className="col-auto">
                <input
                  type="text"
                  id="carrier_agent_id"
                  className="form-control"
                  value={carrier_agent_id || ''}
                  onChange={(e) => handleChange(e)}
                />
              </div>
            </div>
          </div>
          {editing && (
          <div className="col-6">
            <div className="d-flex row align-items-center my-2">
              <div className="col-1" style={{ minWidth: '10.1rem' }}>
                <label htmlFor="active">
                  Active<span className="text-danger">*</span>
                </label>
              </div>
              <div className="col-auto">
                <select
                  id="active"
                  className="form-select"
                  value={active}
                  onChange={(e) => handleChange(e)}
                >
                  <option />
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              </div>
            </div>
            <div className="d-flex row align-items-center my-2">
              <div className="col-1" style={{ minWidth: '10.1rem' }}>
                <label htmlFor="active_date">
                  Active Date<span className="text-danger">*</span>
                </label>
              </div>
              <div className="col-auto">
                <input
                  id="active_date"
                  type="date"
                  className="form-control"
                  value={active_date || ''}
                  onChange={(e) => handleChange(e)}
                />
              </div>
            </div>
          </div>
          )}
        </div>
        <hr />
        <div className="row">
          <div className="col-auto">
            <span className="mx-2 fs-4">Products</span>
          </div>
          <div className="col">
            <div className="row">
              <div className="col">
                <Button label="MA" id="product_ma" value={product_ma} extraClasses="me-2" />
                <Button label="MAPD" id="product_mapd" value={product_mapd} extraClasses="mx-2" />
                <Button label="PDP" id="product_pdp" value={product_pdp} extraClasses="mx-2" />
                <Button label="Medigap" id="product_medigap" value={product_medigap} extraClasses="mx-2" disabled />
              </div>
            </div>
            <div className="row mt-3">
              <div className="col">
                <button
                  type="button"
                  className="btn btn-outline-secondary me-2"
                  style={{ borderRadius: '25px' }}
                  id="product_select_all"
                  onClick={(e) => handleAllProducts(e)}
                >
                  Select All
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary mx-2"
                  style={{ borderRadius: '25px' }}
                  id="product_deselect_all"
                  onClick={(e) => handleAllProducts(e)}
                >
                  Deselect All
                </button>
              </div>
            </div>
          </div>
        </div>
        <hr />
        <div className="row pb-3">
          <div className="col-auto">
            <span className="ms-2 fs-4">States</span>
          </div>
          <div className="col">
            <div className="row">
              <div className="col">
                {allStates.map(state => <Button label={state} id={state} key={state} value={states.includes(state)} buttonWidth="3rem" extraClasses="m-1" state />)}
              </div>
            </div>
            <div className="row mt-3">
              <div className="col">
                <button
                  type="button"
                  className="btn btn-outline-secondary me-2"
                  style={{ borderRadius: '25px' }}
                  id="states_select_all"
                  onClick={(e) => handleAllStates(e)}
                >
                  Select All
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary mx-2"
                  style={{ borderRadius: '25px' }}
                  id="states_deselect_all"
                  onClick={(e) => handleAllStates(e)}
                >
                  Deselect All
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
